import { useRef, useState } from 'react'
import BotPrompt from './BotPrompt'
import { FieldWithEvidence, ListWithEvidence } from './EvidencePanel'
import ChunkSettingsPanel from './ChunkSettingsPanel'

export const C = {
  bg:         '#05050d',
  bg2:        '#08081a',
  surface:    'rgba(255,255,255,0.028)',
  surface2:   'rgba(255,255,255,0.04)',
  border:     'rgba(180,185,230,0.09)',
  borderHi:   'rgba(180,185,230,0.2)',
  silver:     '#b8bdd4',
  silverLo:   '#8890aa',
  silverHi:   '#dce0f0',
  silverGlow: 'rgba(180,185,230,0.12)',
  green:      '#4dc88a',
  orange:     '#e09858',
  red:        '#d45a5a',
  purple:     '#9b7fd4',
  text:       '#dce0f0',
  textMuted:  '#8890aa',
  accent:     '#b8bdd4',
  accentDim:  'rgba(180,185,230,0.12)',
}

export const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

const safeStr = (v) =>
  typeof v === 'string' ? v :
  typeof v === 'number' ? String(v) :
  v?.pattern || v?.value || v?.name || (v ? JSON.stringify(v) : '')

export function GlassPanel({ children, style: extraStyle = {} }) {
  const ref = useRef(null)
  const [mouse, setMouse] = useState({ x: 0, y: 0, on: false })
  return (
    <div
      ref={ref}
      onMouseMove={(e) => {
        const r = ref.current.getBoundingClientRect()
        setMouse({ x: e.clientX - r.left, y: e.clientY - r.top, on: true })
      }}
      onMouseLeave={() => setMouse(m => ({ ...m, on: false }))}
      style={{
        position: 'relative',
        background: 'rgba(255,255,255,0.028)',
        border: `1px solid ${mouse.on ? 'rgba(180,185,230,0.2)' : 'rgba(180,185,230,0.09)'}`,
        borderRadius: 12,
        overflow: 'hidden',
        transition: 'border-color 0.4s',
        ...extraStyle,
      }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>
    </div>
  )
}

export function RadarChart({ data, size = 220 }) {
  if (!data) return null
  const cx = size / 2, cy = size / 2, r = size * 0.34
  const axes = [
    { label: 'Открытость',   val: (data.openness || 0) / 100 },
    { label: 'Добросов.',    val: (data.conscientiousness || 0) / 100 },
    { label: 'Экстраверсия', val: (data.extraversion || 0) / 100 },
    { label: 'Доброжел.',    val: (data.agreeableness || 0) / 100 },
    { label: 'Стабильность', val: (100 - (data.neuroticism || 0)) / 100 },
  ]
  const n = axes.length
  const ang = (i) => (i * 2 * Math.PI / n) - Math.PI / 2
  const pt = (i, ratio) => ({
    x: cx + r * ratio * Math.cos(ang(i)),
    y: cy + r * ratio * Math.sin(ang(i)),
  })
  const mkPath = (pts) => pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join('') + 'Z'
  const dataPath = mkPath(axes.map((a, i) => pt(i, a.val)))

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible' }}>
      {[0.25, 0.5, 0.75, 1].map(ratio => (
        <path key={ratio} d={mkPath(Array.from({ length: n }, (_, i) => pt(i, ratio)))}
          fill="none" stroke="rgba(180,185,230,0.1)" strokeWidth="1" />
      ))}
      {Array.from({ length: n }, (_, i) => { const p = pt(i, 1); return (
        <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="rgba(180,185,230,0.08)" strokeWidth="1" />
      )})}
      <path d={dataPath} fill="rgba(180,185,230,0.1)" stroke="#b8bdd4" strokeWidth="1.5" />
      {axes.map((a, i) => { const p = pt(i, a.val); return (
        <circle key={i} cx={p.x} cy={p.y} r="3" fill="#dce0f0" />
      )})}
      {axes.map((a, i) => { const p = pt(i, 1.28); return (
        <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle"
          fill="#8890aa" fontSize="9" fontFamily="Inter,sans-serif">{a.label}</text>
      )})}
    </svg>
  )
}

// Big Five stat key → index in the bar array
const BIG5_KEYS = ['openness', 'conscientiousness', 'extraversion', 'agreeableness', 'stability']

export default function ProfileResult({
  profile,
  isOwner = false, userId = 'user',
  activeTab, onTabChange,
  isMobile = false, sourceWeights, onWeightsChange,
  learningBonuses = {},
}) {
  const [showTraining, setShowTraining] = useState(false)
  const tab = activeTab || 'personality'
  const setTab = onTabChange || (() => {})

  const p    = profile?.personality_data || profile?.personality
  const comp = profile?.competencies_data || profile?.competencies
  const car  = profile?.career_data || profile?.career
  const mot  = profile?.motivation_data
  const style = profile?.style_analysis
  const ev   = profile?.evidence_data || profile?._evidence || {}

  return (
    <div style={{ fontFamily: font.body, fontSize: 14, color: C.text }}>

      {tab === 'personality' && p && (
        <div>
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.22em', color: C.textMuted, textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
              Тип личности
              {ev.personality?.mbti?.confidence && (
                <span style={{ color: C.green }}>● {ev.personality.mbti.confidence}% уверенность</span>
              )}
            </div>
            <div style={{ fontFamily: font.heading, fontSize: isMobile ? 54 : 80, fontWeight: 300, color: C.silverHi, lineHeight: 1, letterSpacing: '0.08em', marginBottom: 4 }}>
              {p.mbti}
            </div>
            {ev.personality?.mbti && (
              <div style={{ marginBottom: 6 }}>
                <FieldWithEvidence
                  label="Архетип" value={p.archetype}
                  ev={ev.personality.mbti}
                />
              </div>
            )}
            {!ev.personality?.mbti && (
              <div style={{ fontFamily: font.heading, fontSize: isMobile ? 18 : 26, fontStyle: 'italic', fontWeight: 300, color: C.accent, marginBottom: 16 }}>
                {p.archetype}
              </div>
            )}
            <p style={{ color: C.textMuted, lineHeight: 1.75, fontSize: 14, maxWidth: 680, margin: 0 }}>{safeStr(p.summary)}</p>
          </div>

          {p.big_five && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <GlassPanel style={{ padding: '22px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
                  <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase' }}>Big Five</div>
                  {Object.keys(learningBonuses).length > 0 && (
                    <button onClick={() => setShowTraining(s => !s)} style={{
                      padding: '3px 10px', borderRadius: 8, border: `1px solid ${showTraining ? 'rgba(100,220,150,0.4)' : C.border}`,
                      background: showTraining ? 'rgba(100,220,150,0.1)' : 'transparent',
                      color: showTraining ? 'rgba(100,220,150,0.9)' : C.silverLo,
                      fontFamily: font.mono, fontSize: 9, cursor: 'pointer', transition: 'all 0.2s',
                    }}>
                      {showTraining ? '✓ с обучением' : '○ с обучением'}
                    </button>
                  )}
                </div>
                {[
                  ['Закрытость',     'Открытость',         p.big_five.openness,           'openness'],
                  ['Импульсивность', 'Добросовестность',   p.big_five.conscientiousness,  'conscientiousness'],
                  ['Интроверсия',    'Экстраверсия',       p.big_five.extraversion,       'extraversion'],
                  ['Скептицизм',     'Доброжелательность', p.big_five.agreeableness,      'agreeableness'],
                  ['Тревожность',    'Стабильность',       100 - (p.big_five.neuroticism || 0), 'stability'],
                ].map(([lo, hi, val, key]) => {
                  const bonus = (showTraining && learningBonuses[key]) ? Math.min(learningBonuses[key], 100 - (val || 0)) : 0
                  const displayVal = val + bonus
                  return (
                    <div key={hi} style={{ marginBottom: 18 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 7 }}>
                        <span style={{ fontSize: 11, color: C.textMuted }}>{lo}</span>
                        <span style={{ fontSize: 15, fontFamily: font.mono, color: bonus > 0 ? 'rgba(100,220,150,0.9)' : C.silverHi, fontWeight: 700 }}>
                          {bonus > 0 ? `${displayVal} (+${bonus})` : val}
                        </span>
                        <span style={{ fontSize: 11, color: C.accent }}>{hi}</span>
                      </div>
                      <div style={{ height: 4, background: 'rgba(180,185,230,0.08)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                        <div style={{ height: '100%', width: `${val}%`, background: 'linear-gradient(90deg, rgba(180,185,230,0.2), #b8bdd4)', borderRadius: 4, transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)', position: 'absolute', left: 0 }} />
                        {bonus > 0 && (
                          <div style={{ height: '100%', width: `${bonus}%`, background: 'linear-gradient(90deg, rgba(100,220,150,0.6), rgba(100,220,150,0.9))', borderRadius: 4, position: 'absolute', left: `${val}%`, transition: 'width 0.8s ease' }} />
                        )}
                      </div>
                    </div>
                  )
                })}
                {showTraining && (learningBonuses.communication || learningBonuses.leadership || learningBonuses.confidence) && (
                  <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                    <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>Дополнительный прогресс</div>
                    {[['communication','Коммуникация'],['leadership','Лидерство'],['confidence','Уверенность']].map(([k, label]) =>
                      learningBonuses[k] ? (
                        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                          <span style={{ fontSize: 12, color: C.textMuted, fontFamily: font.body, flex: 1 }}>{label}</span>
                          <span style={{ fontFamily: font.mono, fontSize: 11, color: 'rgba(100,220,150,0.9)' }}>+{learningBonuses[k]}</span>
                          <div style={{ width: 60, height: 3, background: 'rgba(180,185,230,0.08)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(learningBonuses[k] * 3, 100)}%`, background: 'rgba(100,220,150,0.7)', borderRadius: 3 }} />
                          </div>
                        </div>
                      ) : null
                    )}
                  </div>
                )}
              </GlassPanel>

              <GlassPanel style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase', marginBottom: 12, alignSelf: 'flex-start' }}>Профиль личности</div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <RadarChart data={showTraining ? {
                    ...p.big_five,
                    openness:        Math.min(100, (p.big_five.openness || 0) + (learningBonuses.openness || 0)),
                    conscientiousness: Math.min(100, (p.big_five.conscientiousness || 0) + (learningBonuses.conscientiousness || 0)),
                    extraversion:    Math.min(100, (p.big_five.extraversion || 0) + (learningBonuses.extraversion || 0)),
                    agreeableness:   Math.min(100, (p.big_five.agreeableness || 0) + (learningBonuses.agreeableness || 0)),
                    neuroticism:     Math.max(0, (p.big_five.neuroticism || 0) - (learningBonuses.stability || 0)),
                  } : p.big_five} size={isMobile ? 190 : 220} />
                </div>
              </GlassPanel>
            </div>
          )}

          {p.core_values?.length > 0 && (
            <GlassPanel style={{ padding: '18px 24px', marginBottom: 16 }}>
              <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase', marginBottom: 14 }}>Ценности</div>
              {ev.personality?.core_values ? (
                <ListWithEvidence
                  label={null}
                  items={p.core_values}
                  evidenceItems={ev.personality.core_values}
                />
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(p.core_values || []).map((v, i) => (
                    <span key={i} style={{ padding: '5px 16px', borderRadius: 20, background: 'rgba(180,185,230,0.07)', border: '1px solid rgba(180,185,230,0.16)', fontSize: 13, color: C.text, fontFamily: font.body }}>
                      {typeof v === 'string' ? v : v?.value || v?.name || ''}
                    </span>
                  ))}
                </div>
              )}
            </GlassPanel>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 16 }}>
            {p.communication_style && (
              <GlassPanel style={{ padding: '20px 22px' }}>
                <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase', marginBottom: 14 }}>Стиль общения</div>
                <p style={{ color: C.text, lineHeight: 1.7, fontSize: 13, margin: 0 }}>{safeStr(p.communication_style)}</p>
              </GlassPanel>
            )}
            {p.decision_making && (
              <GlassPanel style={{ padding: '20px 22px' }}>
                <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase', marginBottom: 14 }}>Принятие решений</div>
                <p style={{ color: C.text, lineHeight: 1.7, fontSize: 13, margin: 0 }}>{safeStr(p.decision_making)}</p>
              </GlassPanel>
            )}
            {(p.strengths?.length > 0 || p.growth_areas?.length > 0) && (
              <GlassPanel style={{ padding: '20px 22px' }}>
                {p.strengths?.length > 0 && (
                  <>
                    <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase', marginBottom: 10 }}>Сильные стороны</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
                      {(p.strengths || []).map((s, i) => (
                        <span key={i} style={{ fontSize: 12, color: C.green, background: 'rgba(77,200,138,0.08)', border: '1px solid rgba(77,200,138,0.18)', padding: '3px 10px', borderRadius: 12 }}>
                          {typeof s === 'string' ? s : s?.value || s?.name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                {p.growth_areas?.length > 0 && (
                  <>
                    <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.2em', color: C.textMuted, textTransform: 'uppercase', marginBottom: 10 }}>Зоны роста</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(p.growth_areas || []).map((g, i) => (
                        <span key={i} style={{ fontSize: 12, color: '#e09858', background: 'rgba(224,152,88,0.08)', border: '1px solid rgba(224,152,88,0.2)', padding: '3px 10px', borderRadius: 12 }}>
                          {typeof g === 'string' ? g : g?.value || g?.name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </GlassPanel>
            )}
          </div>
        </div>
      )}

      {tab !== 'personality' && (
        <GlassPanel style={{ padding: '20px 24px', marginTop: 4 }}>
          <div style={{ fontSize: 14 }}>

            {tab === 'competencies' && comp && (
              <div>
                <ListWithEvidence
                  label="Технические навыки"
                  items={comp.technical_skills}
                  evidenceItems={ev.competencies?.technical_skills}
                />
                <ListWithEvidence
                  label="Мягкие навыки"
                  items={comp.soft_skills}
                  evidenceItems={ev.competencies?.soft_skills}
                />
                {(comp.top_competencies || []).map((c, i) => (
                  <div key={i} style={{ marginBottom: 10, padding: '8px 12px', background: C.bg, borderRadius: 6 }}>
                    <strong>{c.name}</strong>
                    {c.level && <span style={{ color: C.accent, marginLeft: 8 }}>({c.level})</span>}
                    {c.evidence && <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{typeof c.evidence === 'string' ? c.evidence : c.evidence?.pattern || ''}</div>}
                  </div>
                ))}
              </div>
            )}

            {tab === 'career' && car && (
              <div>
                <div style={{ marginBottom: 12 }}>
                  <strong style={{ color: C.accent }}>Идеальные роли:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                    {(car.ideal_roles || []).map((r, i) => <li key={i}>{safeStr(r)}</li>)}
                  </ul>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong style={{ color: C.accent }}>Долгосрочное видение:</strong>
                  <p style={{ color: C.textMuted, marginTop: 4 }}>{safeStr(car.long_term_vision)}</p>
                </div>
                <div>
                  <strong style={{ color: C.accent }}>План действий:</strong>
                  <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
                    {(car.action_plan || []).map((a, i) => <li key={i} style={{ marginBottom: 4 }}>{safeStr(a)}</li>)}
                  </ol>
                </div>
              </div>
            )}

            {tab === 'motivation' && mot && (
              <div>
                {mot.purpose_statement && (
                  <div style={{ marginBottom: 16, padding: '12px 16px', background: C.accentDim, borderRadius: 8, border: `1px solid ${C.accent}40` }}>
                    <strong style={{ color: C.accent, fontSize: 13 }}>Миссия · Purpose</strong>
                    <p style={{ color: C.text, marginTop: 6, marginBottom: 0, lineHeight: 1.6 }}>{mot.purpose_statement}</p>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                  {mot.primary_drivers?.length > 0 && (
                    <div>
                      <strong style={{ color: C.accent }}>Главные драйверы</strong>
                      <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                        {mot.primary_drivers.map((d, i) => <li key={i} style={{ marginBottom: 3 }}>{safeStr(d)}</li>)}
                      </ul>
                    </div>
                  )}
                  {mot.demotivators?.length > 0 && (
                    <div>
                      <strong style={{ color: C.accent }}>Демотиваторы</strong>
                      <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                        {mot.demotivators.map((d, i) => <li key={i} style={{ marginBottom: 3, color: C.textMuted }}>{safeStr(d)}</li>)}
                      </ul>
                    </div>
                  )}
                  {mot.intrinsic_motivators?.length > 0 && (
                    <div>
                      <strong style={{ color: C.accent }}>Внутренняя мотивация</strong>
                      <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                        {mot.intrinsic_motivators.map((m, i) => <li key={i} style={{ marginBottom: 3 }}>{safeStr(m)}</li>)}
                      </ul>
                    </div>
                  )}
                  {mot.extrinsic_motivators?.length > 0 && (
                    <div>
                      <strong style={{ color: C.accent }}>Внешняя мотивация</strong>
                      <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                        {mot.extrinsic_motivators.map((m, i) => <li key={i} style={{ marginBottom: 3 }}>{safeStr(m)}</li>)}
                      </ul>
                    </div>
                  )}
                  {mot.energy_sources?.length > 0 && (
                    <div>
                      <strong style={{ color: C.accent }}>Источники энергии</strong>
                      <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                        {mot.energy_sources.map((e, i) => <li key={i} style={{ marginBottom: 3 }}>{safeStr(e)}</li>)}
                      </ul>
                    </div>
                  )}
                  {mot.values_in_action?.length > 0 && (
                    <div>
                      <strong style={{ color: C.accent }}>Ценности в действии</strong>
                      <ul style={{ margin: '6px 0', paddingLeft: 18 }}>
                        {mot.values_in_action.map((v, i) => <li key={i} style={{ marginBottom: 3 }}>{safeStr(v)}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'motivation' && !mot && (
              <p style={{ color: C.textMuted, fontSize: 13 }}>Данные мотивации появятся после повторного анализа</p>
            )}

            {tab === 'style' && style && (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
                  {[
                    ['Ср. длина сообщения', `${style.avg_message_length} символов`],
                    ['Использование эмодзи', `${style.emoji_usage}%`],
                    ['Формальность речи', `${style.formality_score}/100`],
                    ['Всего сообщений', style.total_messages],
                  ].map(([k, v]) => (
                    <div key={k} style={{ background: C.bg, padding: '10px 12px', borderRadius: 6 }}>
                      <div style={{ fontSize: 11, color: C.textMuted }}>{k}</div>
                      <div style={{ fontSize: 18, color: C.accent, fontFamily: font.heading }}>{v}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <strong style={{ color: C.accent }}>Паттерны:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                    {(style.sentence_patterns || []).map((p, i) => <li key={i}>{safeStr(p)}</li>)}
                  </ul>
                </div>
                <div style={{ marginTop: 12 }}>
                  <strong style={{ color: C.accent }}>Топ слов:</strong>
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(style.vocabulary || []).slice(0, 20).map((v, i) => (
                      <span key={i} style={{ background: C.accentDim, border: `1px solid ${C.border}`, padding: '1px 6px', borderRadius: 4, fontSize: 11 }}>
                        {v.word} <span style={{ color: C.textMuted }}>×{v.count}</span>
                      </span>
                    ))}
                  </div>
                </div>
                {isOwner && (
                  <BotPrompt userId={userId} initialPrompt={profile?._botPrompt} />
                )}
              </div>
            )}

            {tab === 'raw' && (
              <div>
                {isOwner && <ChunkSettingsPanel userId={userId} />}
                <pre style={{ fontSize: 11, overflow: 'auto', color: C.textMuted, maxHeight: 400 }}>
                  {JSON.stringify(profile, null, 2)}
                </pre>
              </div>
            )}

          </div>
        </GlassPanel>
      )}
    </div>
  )
}
